import { useCallback, useRef, useState } from "react";
import {
  calculateBoxDimensions,
  createCopiedBox,
  createNewBox,
  getRelativeCoordinates,
  isBoxLargeEnough,
  saveDraft,
} from "../utils";

export const useGetWorksheetFunctions = (
  boxes,
  onBoxesChange,
  isAdmin,
  exersiceDetails,
  isExerciseAttempted = false
) => {
  const [selectedBoxId, setSelectedBoxId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createStart, setCreateStart] = useState(null);
  const [previewBox, setPreviewBox] = useState(null);

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  const handleBoxSelect = useCallback((boxId) => {
    setSelectedBoxId(boxId);
  }, []);

  const handleBoxUpdate = useCallback(
    (boxId, updates) => {
      const boxToUpdate = boxes.find((box) => box.id === boxId);

      const updatedBoxes = boxes.map((box) => {
        if (
          boxToUpdate.type === "single-select" &&
          box.type === "single-select" &&
          box.id !== boxId &&
          box.questionId === boxToUpdate.questionId
        ) {
          return {
            ...box,
            answer:
              box.answer === null
                ? box.userSubmittedAnswer !== "" &&
                  box.userSubmittedAnswer !== null
                  ? false
                  : null
                : false,
          };
        }

        return box.id === boxId
          ? { ...box, ...updates, lastAnswer: box.answer }
          : box;
      });

      if (
        ["single-select", "multi-select"].includes(boxToUpdate.type) &&
        !(exersiceDetails?.isExerciseAttempted || isExerciseAttempted)
      ) {
        saveDraft(
          {
            ...boxToUpdate,
            ...updates,
          },
          updatedBoxes,
          exersiceDetails
        );
      }

      onBoxesChange(updatedBoxes);
    },
    [boxes, exersiceDetails, isExerciseAttempted, onBoxesChange]
  );

  const handleBoxDelete = useCallback(
    (boxId) => {
      const updatedBoxes = boxes.filter((box) => box.id !== boxId);
      onBoxesChange(updatedBoxes);
      setSelectedBoxId(null);
    },
    [boxes, onBoxesChange]
  );

  const handleBoxCopy = useCallback(
    (boxId) => {
      const boxToCopy = boxes.find((box) => box.id === boxId);
      if (!boxToCopy) return;

      const newBox = createCopiedBox(boxToCopy);
      onBoxesChange([...boxes, newBox]);
      setSelectedBoxId(newBox.id);
    },
    [boxes, onBoxesChange]
  );

  const handleContainerDoubleClick = useCallback(
    (e) => {
      if (!isAdmin) return;

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const coordinates = getRelativeCoordinates(e, containerRect);
      setCreateStart(coordinates);
      setIsCreating(true);
    },
    [isAdmin]
  );

  const handleContainerMouseMove = useCallback(
    (e) => {
      if (!isCreating || !createStart) return;

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const currentCoords = getRelativeCoordinates(e, containerRect);
      const dimensions = calculateBoxDimensions(createStart, currentCoords);

      setPreviewBox(dimensions);
    },
    [isCreating, createStart]
  );

  const handleContainerMouseUp = useCallback(
    (e) => {
      if (!isCreating || !createStart) return;

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const currentCoords = getRelativeCoordinates(e, containerRect);
      const dimensions = calculateBoxDimensions(createStart, currentCoords);

      // Create box only if it meets minimum size requirements
      if (isBoxLargeEnough(dimensions)) {
        const newBox = createNewBox(dimensions);
        onBoxesChange([...boxes, newBox]);
        setSelectedBoxId(newBox.id);
      }

      // Reset creation state
      setIsCreating(false);
      setCreateStart(null);
      setPreviewBox(null);
    },
    [isCreating, createStart, boxes, onBoxesChange]
  );

  const handleContainerClick = useCallback((e) => {
    if (containerRef.current && containerRef.current.contains(e.target)) {
      setSelectedBoxId(null);
    }
  }, []);

  const handleSaveDraft = useCallback(
    (box) => {
      if (
        !["single-select", "multi-select"].includes(box.type) &&
        !(exersiceDetails?.isExerciseAttempted || isExerciseAttempted)
      ) {
        saveDraft(box, boxes, exersiceDetails);
      }
    },
    [boxes, exersiceDetails, isExerciseAttempted]
  );

  return {
    methods: {
      // Box management
      handleBoxSelect,
      handleBoxUpdate,
      handleBoxDelete,
      handleBoxCopy,
      handleSaveDraft,

      // Container interactions
      handleContainerDoubleClick,
      handleContainerMouseMove,
      handleContainerMouseUp,
      handleContainerClick,
    },
    states: {
      selectedBoxId,
      setSelectedBoxId,
      isCreating,
      setIsCreating,
      createStart,
      setCreateStart,
      previewBox,
      setPreviewBox,
    },
    refs: {
      containerRef,
      imageRef,
    },
  };
};
